import type { Task, TaskStatus } from '../store/seedanceTypes';

/** 兼容不同后端状态枚举，统一映射为前端任务状态。 */
export const normalizeStatus = (s: string): TaskStatus => {
  const map: Record<string, TaskStatus> = {
    queued: 'pending',
    pending: 'pending',
    in_progress: 'processing',
    running: 'processing',
    processing: 'processing',
    completed: 'succeeded',
    succeeded: 'succeeded',
    success: 'succeeded',
    done: 'succeeded',
    finished: 'succeeded',
    failed: 'failed',
    fail: 'failed',
    failure: 'failed',
    error: 'failed',
    expired: 'failed',
  };
  return map[String(s || '').toLowerCase()] || 'pending';
};

/** 从多种任务响应结构中提取提示词文本。 */
export const extractPrompt = (data: any): string => {
  if (data?.prompt) return data.prompt;
  const content = data?.content;
  if (!content) return '无描述';
  if (Array.isArray(content)) {
    const t = content.find((c: any) => c && c.type === 'text');
    if (t && t.text) return t.text;
  } else if (typeof content === 'object') {
    if (content.text) return content.text;
  }
  return '无描述';
};

/** 云端返回的视频 URL 通常仅短期有效（与产品说明一致，用于列表 UI 隐藏失效预览区）。 */
export const TASK_VIDEO_URL_TTL_MS = 24 * 60 * 60 * 1000;

/** 任务列表仅展示创建时间在最近 N 毫秒内的记录（与视频链接时效说明一致）。 */
export const TASK_LIST_DISPLAY_MAX_AGE_MS = TASK_VIDEO_URL_TTL_MS;

/** /api/task/self 时间范围：Unix 秒 [start, end]，覆盖「当前时刻」往前 TASK_LIST_DISPLAY_MAX_AGE_MS。 */
export function getTaskListRecentRangeUnixSeconds(): { startTimestamp: number; endTimestamp: number } {
  const endTimestamp = Math.floor(Date.now() / 1000);
  const startTimestamp = endTimestamp - Math.floor(TASK_LIST_DISPLAY_MAX_AGE_MS / 1000);
  return { startTimestamp, endTimestamp };
}

type TaskLikeForVideoExpiry = {
  status: string;
  finished_at?: string;
  completed_at?: string;
  created_at?: string;
};

/** 任务完成时间（优先结束时间，避免用 updated_at 以免随列表刷新变化）。 */
export const getTaskVideoCompletionTimeMs = (task: TaskLikeForVideoExpiry): number | null => {
  return (
    parseTaskTimeToMs(task.finished_at) ??
    parseTaskTimeToMs(task.completed_at) ??
    parseTaskTimeToMs(task.created_at)
  );
};

/** 已成功且已超过可播放时长：不展示视频区与按钮（链接多已 404）。 */
export const isTaskVideoUrlExpired = (task: TaskLikeForVideoExpiry): boolean => {
  if (task.status !== 'succeeded') return false;
  const endMs = getTaskVideoCompletionTimeMs(task);
  if (endMs === null) return false;
  return Date.now() - endMs >= TASK_VIDEO_URL_TTL_MS;
};

/** 过滤失败占位 URL，只保留可直接播放/下载的视频地址。 */
export const isUsableVideoUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized.startsWith('http') && !normalized.includes('failed');
};

/** 兼容秒级时间戳、毫秒时间戳和 ISO 字符串。 */
export const parseTaskTimeToMs = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value <= 0) return null;
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return null;
    if (/^-?\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      if (!Number.isFinite(n) || n <= 0) return null;
      return n < 1e12 ? n * 1000 : n;
    }
    const d = new Date(s);
    const t = d.getTime();
    return Number.isNaN(t) ? null : t;
  }
  return null;
};

/** 按优先级选择第一个有效任务时间，并转成 ISO 字符串。 */
export const mergeTaskTimestamps = (...vals: unknown[]): string | undefined => {
  for (const v of vals) {
    const ms = parseTaskTimeToMs(v);
    if (ms !== null) return new Date(ms).toISOString();
  }
  return undefined;
};

/** 任务列表展示时间格式化。 */
export const formatTaskListTime = (raw: unknown): string => {
  const ms = parseTaskTimeToMs(raw);
  if (ms === null) return '未知时间';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '未知时间';
  return d.toLocaleString('zh-CN');
};

/** 清理文件名中的非法字符，供下载命名使用。 */
export const sanitizeDownloadFileStem = (name: string): string =>
  name
    .replace(/[/\\?%*:|"<>]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 80) || 'seedance-video';

/** 确保下载文件名带视频扩展名。 */
export const ensureVideoDownloadFileName = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return 'seedance-video.mp4';
  if (/\.(mp4|webm|mov|m4v)$/i.test(trimmed)) return trimmed;
  return `${trimmed.replace(/\.[^./\\]+$/, '')}.mp4`;
};

/** 任务视频下载默认文件名：优先用户自定义任务名，否则用任务时间。 */
export const defaultTaskVideoDownloadName = (
  task: Pick<
    Task,
    | 'prompt'
    | 'remoteTaskId'
    | 'id'
    | 'created_at'
    | 'finished_at'
    | 'updated_at'
    | 'completed_at'
    | 'status'
  >,
  options?: { customTaskName?: string },
): string => {
  const custom = (options?.customTaskName || '').trim();
  if (custom) return sanitizeDownloadFileStem(custom);

  const timeRaw =
    task.status === 'succeeded' || task.status === 'failed'
      ? task.finished_at || task.completed_at || task.updated_at || task.created_at
      : task.updated_at || task.created_at || task.finished_at || task.completed_at;
  const timeLabel = formatTaskListTime(timeRaw);
  if (timeLabel !== '未知时间') {
    return sanitizeDownloadFileStem(timeLabel);
  }
  const id = (task.remoteTaskId || task.id || 'video').replace(/[^\w-]/g, '_').slice(0, 20);
  return sanitizeDownloadFileStem(`seedance_${id}`);
};

/**
 * 是否可用「同源媒体代理」下载。
 *
 * 本地开发（setupProxy.js）与线上部署（Nginx）都提供 `/proxy/media-download/<完整URL>`
 * 路径式代理，因此只要页面是通过 http(s) 提供（非 file:// 直接打开）即可使用。
 */
const canUseLocalMediaProxy = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.protocol === 'http:' || window.location.protocol === 'https:';
};

/**
 * 构造同源媒体代理地址（路径式，便于原版 Nginx 正则反代）。
 * 注意：直接拼接完整 URL，不做 encodeURIComponent，
 * 这样 TOS 签名里的 `?`/`&` 会自然成为请求的 query，原样透传给上游保签名。
 */
const buildMediaProxyUrl = (sourceUrl: string): string =>
  `/proxy/media-download/${sourceUrl}`;

const getElectronDownloadFile = ():
  | ((url: string, fileName: string) => Promise<{ canceled: boolean; filePath?: string }>)
  | null => {
  const fn = window.electronAPI?.downloadFile;
  return typeof fn === 'function' ? fn.bind(window.electronAPI) : null;
};

const isVideoContentType = (ct: string): boolean => {
  const lower = ct.toLowerCase();
  return (
    lower.startsWith('video/') ||
    lower === 'application/octet-stream' ||
    lower.startsWith('binary/') ||
    lower === ''
  );
};

/**
 * 同源媒体代理取流（兜底）：本地走 setupProxy.js，线上走 Nginx。
 * 代理在服务端校验内容类型并设置 attachment；仅当直连 CDN 不可用（无 CORS）时使用。
 */
const fetchVideoViaProxy = async (sourceUrl: string): Promise<Response> => {
  if (!canUseLocalMediaProxy()) {
    throw new Error('当前环境无法下载视频，请通过 http(s) 访问页面后重试');
  }
  const proxyUrl = buildMediaProxyUrl(sourceUrl);
  let resp: Response;
  try {
    resp = await fetch(proxyUrl, { cache: 'no-store' });
  } catch (e) {
    throw new Error(e instanceof Error ? `下载失败：${e.message}` : '下载失败：网络错误');
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(text.trim() || `下载失败：服务器返回 HTTP ${resp.status}`);
  }
  const ct = resp.headers.get('content-type') || '';
  if (!isVideoContentType(ct)) {
    // 多半是链接已过期 / 返回了网页 HTML（如 VOD 预览页）—— 给出可定位的提示
    throw new Error(
      `下载失败：服务器返回的不是视频内容（${ct || '未知类型'}）。` +
        '请确认视频链接是否已过期，或下载代理是否已正确配置。',
    );
  }
  return resp;
};

/**
 * 直连 CDN 取流（优先）：火山 TOS/VOD 会按请求 Origin 回 CORS 头，浏览器可直接读取。
 * 失败（无 CORS / 网络错误 / 非视频内容）时返回 null，由调用方走代理兜底。
 */
const fetchVideoDirect = async (sourceUrl: string): Promise<Response | null> => {
  try {
    const r = await fetch(sourceUrl, { credentials: 'omit', cache: 'no-store' });
    if (r.ok && r.body && isVideoContentType(r.headers.get('content-type') || '')) {
      return r;
    }
  } catch {
    /* CORS / 网络失败 → 交给代理兜底 */
  }
  return null;
};

/** 取视频响应：直连优先，代理兜底。 */
const fetchVideoForDownload = async (sourceUrl: string): Promise<Response> => {
  const direct = await fetchVideoDirect(sourceUrl);
  if (direct) return direct;
  return fetchVideoViaProxy(sourceUrl);
};

export type VideoDownloadResult = { filePath?: string };

/**
 * 下载远程视频到本地。
 *
 * 1. Electron 环境 → 系统原生「另存为」对话框（dialog.showSaveDialog + net.fetch，无 CORS 限制）
 * 2. 浏览器 → 直连 CDN（CORS）优先、同源代理兜底 → blob + <a download> 自动下载
 *    （文件名由调用方传入；任务列表会先弹「输入文件名」弹窗再调用）
 */
export const triggerVideoDownload = async (
  url: string,
  fileName: string,
): Promise<VideoDownloadResult> => {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) throw new Error('视频地址无效');
  const safeName = ensureVideoDownloadFileName(fileName);

  // 1. Electron 主进程：dialog.showSaveDialog + net.fetch
  const electronDownload = getElectronDownloadFile();
  if (electronDownload) {
    const result = await electronDownload(trimmedUrl, safeName);
    if (result.canceled) throw new Error('已取消下载');
    return { filePath: result.filePath };
  }

  // 2. 浏览器：直连优先、代理兜底 → blob 下载
  const resp = await fetchVideoForDownload(trimmedUrl);
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 延迟释放，确保下载已开始
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return {};
};
